import React, { Component } from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableSortLabel,
  Button,
  TablePagination,
  TextField,
  Box,
  CircularProgress,
} from "@material-ui/core";

import { userService } from "_services/user.service";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";

import { connect } from "react-redux";
import { reduxLoad } from "js/actions/index";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import {
  PER_PAGE_OPTIONS,
  DEFAULT_PER_PAGE_RECORDS,
} from "__helpers/constants";

function mapDispatchToProps(dispatch) {
  return {
    reduxLoad: (reduxLoadVal) => dispatch(reduxLoad(reduxLoadVal)),
  };
}
const mapStateToProps = (state) => {
  return {
    userInfoRedux: state.userInfo,
    timestampRedux: state.timestamp,
    reduxLoadFlag: state.reduxLoadFlag,
  };
};

const headCells = [
  { id: "providerName", numeric: false, sorting: true, label: "Provider Name", width: 220 },
  { id: "totalImpressions", numeric: true, sorting: true, label: "Impressions", width: 120 },
  { id: "totalClicks", numeric: true, sorting: true, label: "Clicks", width: 100 },
  { id: "totalReferrals", numeric: true, sorting: true, label: "Referrals", width: 100 },
  { id: "currentlySponsored", numeric: false, sorting: true, label: "Status", width: 120 },
];

class SponsoredProviderMetricsClass extends Component {
  constructor(props) {
    super(props);
    this.dateUtility = new DateFnsUtils();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.state = {
      loading: false,
      startDate: thirtyDaysAgo,
      endDate: today,
      startDateValid: true,
      endDateValid: true,
      startMaxDate: new Date(),
      providerNameFilter: "",
      reportData: null,
      displayData: [],
      orderBy: "totalImpressions",
      direction: false,
      perPage: DEFAULT_PER_PAGE_RECORDS,
      currentPage: 0,
    };
  }

  componentDidMount() {
    this.handleGenerateReport();
  }

  handleStartDateChange = (date) => {
    const { endDate } = this.state;
    this.setState({ startDate: date, startDateValid: true });
    if (endDate && date > endDate) {
      this.setState({ endDate: null, endDateValid: false });
    }
  };

  handleEndDateChange = (date) => {
    this.setState({ endDate: date, endDateValid: true });
  };

  handleProviderNameChange = (event) => {
    this.setState({ providerNameFilter: event.target.value });
  };

  clientSideSort = (data, orderBy, ascending) => {
    return [...data].sort((a, b) => {
      let valA = a[orderBy];
      let valB = b[orderBy];
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;
      if (typeof valA === "string") {
        return ascending
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return ascending ? valA - valB : valB - valA;
    });
  };

  tableSortHandler = (property) => () => {
    this.setState(
      (prevState) => ({
        orderBy: property,
        direction: prevState.orderBy === property ? !prevState.direction : true,
      }),
      () => {
        const sorted = this.clientSideSort(
          this.state.displayData,
          this.state.orderBy,
          this.state.direction
        );
        this.setState({ displayData: sorted });
      }
    );
  };

  handleChangePage = (event, newPage) => {
    this.setState({ currentPage: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({
      perPage: parseInt(event.target.value),
      currentPage: 0,
    });
  };

  handleGenerateReport = async () => {
    const { startDate, endDate, providerNameFilter } = this.state;

    if (!startDate || !endDate) {
      userService.showNotification({
        title: "Sponsored Provider Metrics",
        message: "Please select both start and end dates.",
        type: "danger",
      });
      return;
    }

    this.setState({ loading: true });
    userService.showNotification({
      title: "Sponsored Provider Metrics",
      message: "Generating report, please wait...",
      type: "info",
    });

    try {
      const queryParams = [];
      queryParams.push(`startDate=${`${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`}`);
      queryParams.push(`endDate=${`${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`}`);
      if (providerNameFilter && providerNameFilter.trim()) {
        queryParams.push(`providerName=${encodeURIComponent(providerNameFilter.trim())}`);
      }
      const url = `${apiPath.sponsoredMetricsReport}?${queryParams.join("&")}`;

      const response = await fetch(url, {
        method: "GET",
        headers: generalAuthTokenHeader(),
      });

      if (!response.ok) {
        throw new Error("Failed to generate report");
      }

      const data = await response.json();

      const sorted = this.clientSideSort(
        data.providers || [],
        this.state.orderBy,
        this.state.direction
      );

      this.setState(
        {
          reportData: data,
          displayData: sorted,
          loading: false,
          currentPage: 0,
        },
        () => {
          userService.showNotification({
            title: "Sponsored Provider Metrics",
            message: `Report generated with ${(data.providers || []).length} sponsored providers.`,
            type: "success",
          });
        }
      );
    } catch (error) {
      console.error("Error generating report:", error);
      this.setState({ loading: false });
      userService.showNotification({
        title: "Sponsored Provider Metrics",
        message: "Failed to generate report. Please try again.",
        type: "danger",
      });
    }
  };

  handleDownloadExcel = async () => {
    const { startDate, endDate, providerNameFilter } = this.state;

    userService.showNotification({
      title: "Sponsored Provider Metrics",
      message: "Downloading Excel, please wait...",
      type: "info",
    });

    try {
      const queryParams = [];
      queryParams.push(`startDate=${`${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`}`);
      queryParams.push(`endDate=${`${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`}`);
      if (providerNameFilter && providerNameFilter.trim()) {
        queryParams.push(`providerName=${encodeURIComponent(providerNameFilter.trim())}`);
      }
      const url = `${apiPath.sponsoredMetricsReportDownload}?${queryParams.join("&")}`;
      await userService.fetchsReferralReportData(url);
    } catch (error) {
      console.error("Error downloading report:", error);
      userService.showNotification({
        title: "Sponsored Provider Metrics",
        message: "Failed to download Excel report.",
        type: "danger",
      });
    }
  };

  renderSummaryCards() {
    const { reportData } = this.state;
    if (!reportData) return null;

    const cards = [
      {
        label: "Total Impressions",
        value: reportData.totalImpressions || 0,
        color: "info",
      },
      {
        label: "Total Clicks",
        value: reportData.totalClicks || 0,
        color: "success",
      },
      {
        label: "Total Referrals",
        value: reportData.totalReferrals || 0,
        color: "warning",
      },
    ];

    return (
      <Box display="flex" flexWrap="wrap" style={{ marginBottom: 16, gap: 16 }}>
        {cards.map((card, idx) => (
          <Card
            key={idx}
            style={{
              flex: "1 1 200px",
              minWidth: 200,
              margin: 0,
            }}
          >
            <CardHeader color={card.color} stats>
              <p style={{ fontSize: 14, margin: 0, color: "#fff" }}>
                {card.label}
              </p>
              <h3
                style={{
                  fontSize: 28,
                  fontWeight: "bold",
                  margin: "8px 0 0",
                  color: "#fff",
                }}
              >
                {card.value}
              </h3>
            </CardHeader>
          </Card>
        ))}
      </Box>
    );
  }

  render() {
    const {
      startDate,
      endDate,
      startMaxDate,
      providerNameFilter,
      displayData,
      orderBy,
      direction,
      perPage,
      currentPage,
      reportData,
      loading,
    } = this.state;

    const paginatedData = displayData.slice(
      currentPage * perPage,
      currentPage * perPage + perPage
    );

    return (
      <div>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            {this.renderSummaryCards()}

            <Card>
              <CardHeader color="success" className="cusCardTitle">
                <h4 className="cusCardTitleText">
                  Sponsored Provider Metrics
                </h4>
              </CardHeader>
              <CardBody>
                {/* Filters Row */}
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="flex-end"
                  style={{ gap: 16, marginBottom: 16 }}
                >
                  <Box style={{ minWidth: 160 }}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        label="Start Date *"
                        format="MM/dd/yyyy"
                        margin="normal"
                        id="start-date-picker"
                        name="startDate"
                        value={startDate}
                        maxDate={startMaxDate}
                        onChange={this.handleStartDateChange}
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                          className: "date-picker-span",
                        }}
                      />
                    </MuiPickersUtilsProvider>
                  </Box>

                  <Box style={{ minWidth: 160 }}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        label="End Date *"
                        format="MM/dd/yyyy"
                        margin="normal"
                        id="end-date-picker"
                        value={endDate}
                        maxDate={startMaxDate}
                        minDate={startDate}
                        onChange={this.handleEndDateChange}
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                          className: "date-picker-span",
                        }}
                      />
                    </MuiPickersUtilsProvider>
                  </Box>

                  <Box style={{ minWidth: 200 }}>
                    <TextField
                      label="Provider Name"
                      value={providerNameFilter}
                      onChange={this.handleProviderNameChange}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          this.handleGenerateReport();
                        }
                      }}
                      style={{ marginTop: 16 }}
                    />
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box style={{ marginBottom: 16 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.handleGenerateReport}
                    disabled={!startDate || !endDate || loading}
                  >
                    {loading ? "Generating..." : "Generate Report"}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={this.handleDownloadExcel}
                    disabled={!reportData || loading}
                    style={{ marginLeft: 10, backgroundColor: "#4caf50", color: "#fff" }}
                  >
                    Download Excel
                  </Button>
                </Box>

                {/* Data Table */}
                {loading ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <div style={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {headCells.map((headCell) => (
                              <TableCell
                                key={headCell.id}
                                align={headCell.numeric ? "right" : "left"}
                                style={{
                                  fontWeight: "bold",
                                  fontSize: 13,
                                  minWidth: headCell.width,
                                  padding: "8px 12px",
                                }}
                              >
                                {headCell.sorting ? (
                                  <TableSortLabel
                                    active={orderBy === headCell.id}
                                    direction={direction ? "asc" : "desc"}
                                    onClick={this.tableSortHandler(headCell.id)}
                                  >
                                    {headCell.label}
                                  </TableSortLabel>
                                ) : (
                                  headCell.label
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedData.length ? (
                            paginatedData.map((row) => (
                              <TableRow key={row.providerId}>
                                <TableCell style={{ padding: "8px 12px", fontSize: 13 }}>
                                  {row.providerName}
                                </TableCell>
                                <TableCell align="right" style={{ padding: "8px 12px", fontSize: 13 }}>
                                  {row.totalImpressions || 0}
                                </TableCell>
                                <TableCell align="right" style={{ padding: "8px 12px", fontSize: 13 }}>
                                  {row.totalClicks || 0}
                                </TableCell>
                                <TableCell align="right" style={{ padding: "8px 12px", fontSize: 13 }}>
                                  {row.totalReferrals || 0}
                                </TableCell>
                                <TableCell style={{ padding: "8px 12px", fontSize: 13 }}>
                                  <span style={{
                                    color: row.currentlySponsored ? "#4caf50" : "#9e9e9e",
                                    fontWeight: "bold",
                                  }}>
                                    {row.currentlySponsored ? "Active" : "Inactive"}
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={headCells.length} align="center">
                                <div className="not-found-text">
                                  {reportData
                                    ? "No sponsored providers found for the selected criteria."
                                    : "Select date range and click Generate Report."}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>

                    {displayData.length > 0 && (
                      <TablePagination
                        rowsPerPageOptions={PER_PAGE_OPTIONS}
                        component="div"
                        count={displayData.length}
                        rowsPerPage={perPage}
                        page={currentPage}
                        onChangePage={this.handleChangePage}
                        onChangeRowsPerPage={this.handleChangeRowsPerPage}
                      />
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

export const SponsoredProviderMetrics = connect(
  mapStateToProps,
  mapDispatchToProps
)(SponsoredProviderMetricsClass);

export default SponsoredProviderMetrics;
